<template>
    <ValidationObserver class="login-form" ref="observer" tag="form" v-slot="{ invalid }">
        <div class="mb-3">
            <h5 class="text-center">{{ $t('auth.forgot_your_password') }}</h5>
        </div>

        <FormSlot name="email"
                  label="E-mail"
                  parentClass="form-group form-group-feedback form-group-feedback-left"
                  :showLabel="false"
                  :rules="{required: true, email: true}"
                  v-slot="{errors}"
        >
            <input v-model="email"
                   type="email"
                   ref="email"
                   autocomplete="email"
                   class="form-control"
                   :placeholder="$t('auth.email')"
                   :disabled="processing"
            >
            <div class="form-control-feedback">
                <i class="icon-mail5 text-muted"></i>
            </div>
        </FormSlot>

        <SlideYUpTransition>
            <FormSlot v-if="status > 10"
                      v-slot="{errors}"
                      name="code"
                      :label="$t('auth.security_code')"
                      parentClass="form-group form-group-feedback form-group-feedback-left"
                      :showLabel="false"
                      :rules="{required: true, length: 6}"
            >
                <input v-model="code"
                       type="text"
                       ref="code"
                       autocomplete="off"
                       class="form-control"
                       :placeholder="$t('auth.security_code')"
                       :disabled="status === 20 || processing"
                       @submit="validateCode"
                >
                <div class="form-control-feedback">
                    <i class="icon-calculator2 text-muted"></i>
                </div>
                <span class="form-text text-muted">
                    {{$t('auth.security_code_field_description')}}
                </span>
            </FormSlot>
        </SlideYUpTransition>

        <div v-if="errorSendCode" class="alert bg-danger text-white">
            <span class="font-weight-semibold">{{$t('auth.sending_security_code_error')}}</span>
        </div>

        <div class="form-group text-center">
            <button v-if="processing"
                    disabled="disabled"
                    type="button" class="btn btn-primary btn-block"
            >
                <span v-if="status > 40">{{$t('auth.security_code_verification')}}</span>
                <span v-else>{{$t('auth.sending_security_code')}}</span>
                <fa-icon icon="spinner" spin class="ml-2" />
            </button>

            <div v-else>
                <button v-if="status === 0"
                        :disabled="invalid"
                        class="btn btn-primary btn-block"
                        @click.prevent="sendCode"
                >
                  {{$t('auth.send_security_code')}} <fa-icon icon="paper-plane" class="ml-2" />
                </button>

                <button v-else
                        class="btn btn-link"
                        type="button"
                        @click.prevent="sendCode"
                >
                  {{$t('auth.send_security_code_again')}} <fa-icon icon="paper-plane" class="ml-2" />
                </button>
            </div>
        </div>

        <div v-if="status === 10" class="form-group text-center">
            <router-link :to="{name: 'Login'}" class="btn btn-link">
              {{$t('auth.go_back_to_log_in_page')}}
            </router-link>
        </div>

    </ValidationObserver>
</template>

<script>

    import {ValidationObserver} from "vee-validate";
    import {SlideYUpTransition} from 'vue2-transitions';
    import {FormSlot} from '@dealer-group/dealerui';
    import store from "../store/store";
    import {SEND_CODE, VALIDATE_CODE} from "../store/types";
    import {FLASH_DELAY} from "../constants";

    export default {
        name: "GenerateCode",
        components: {FormSlot, SlideYUpTransition, ValidationObserver},
        computed: {
            ...store.mapFields(['email', 'code']),
            ...store.mapState(['user']),

            emailHasBeenCompleted() {
                return this.$refs.observer.errors.email.length === 0 && this.email !== '';
            }
        },

        watch: {
            code() {
                // przypadek, gdy zmiana kodu wynika z przekazania go przez query string
                if (this.status === 0) {
                    this.status = 40;
                }
                this.$nextTick(() => this.validateCode());
            }
        },

        data: () => ({
            processing: false,
            status: 0,
            errorSendCode: false,
        }),

        mounted() {
            this.focusEmail();
        },

        methods: {
            focusEmail() {
                this.$nextTick(() => this.$refs.email.focus())
            },
            focusCode() {
                this.$nextTick(() => this.$refs.code.focus())
            },

            sendCode() {
                if (! this.emailHasBeenCompleted) {
                    return;
                }

                this.processing = true;
                this.code = null;
                this.status = 20;

                store
                    .dispatch(SEND_CODE)
                    .then(() => {
                        this.code = null;
                        this.status = 40;
                        this.$flash.success('Kod został wysłany, sprawdź swoją skrzynkę e-mail.', {delay: FLASH_DELAY});
                        this.focusCode();
                    })
                    .catch(() => {
                        this.status = 10;
                        this.$flash.error('Błąd wysyłki kodu.', {delay: FLASH_DELAY});
                    })
                    .finally(() => this.processing = false);
            },

            async validateCode() {
                const isValid = await this.$refs.observer.validate();

                if (! isValid || ! this.code || ! this.email) {
                    return;
                }

                this.processing = true;
                this.status = 60;

                store
                    .dispatch(VALIDATE_CODE)
                    .then(() => {
                        this.status = 70;
                        this.$flash.success('Kod jest poprawny, ustaw nowe hasło.', {delay: FLASH_DELAY});
                    })
                    .catch(() => {
                        this.status = 50;
                        this.$refs.observer.setErrors({
                            'code': 'Sprawdź poprawność kodu'
                        });
                    })
                    .finally(() => this.processing = false);
            },
        }
    }
</script>

<style scoped>

</style>
